import axios from 'axios';
import store from '@/state/store';

const DEFAULT_API_PORT = process.env.VUE_APP_API_PORT || '8001';
const PRODUCTION_API_FALLBACK = 'https://rrpl-api.onrender.com';

function isLocalDevHost(hostname) {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    /^192\.168\.\d+\.\d+$/.test(hostname) ||
    /^10\.\d+\.\d+\.\d+$/.test(hostname)
  );
}

/** URL da API: em dev/LAN usa o mesmo host do browser (ex.: 192.168.x.x:8001). */
export function getApiBaseURL() {
  const fromEnv = process.env.VUE_APP_API_BASE_URL;
  if (fromEnv) return String(fromEnv).replace(/\/$/, '');

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const { protocol, hostname } = window.location;
    if (isLocalDevHost(hostname)) {
      return `${protocol}//${hostname}:${DEFAULT_API_PORT}`;
    }
    if (hostname.endsWith('.pages.dev')) {
      console.warn(
        '[RRPL] VUE_APP_API_BASE_URL ausente no build; a usar',
        PRODUCTION_API_FALLBACK,
      );
      return PRODUCTION_API_FALLBACK;
    }
  }

  return `http://127.0.0.1:${DEFAULT_API_PORT}`;
}

export const apiBaseURL = getApiBaseURL();

const api = axios.create({
  baseURL: apiBaseURL,
  timeout: 90000,
  headers: { 'Content-Type': 'application/json' },
});

function readStoredUser() {
  for (const storage of [localStorage, sessionStorage]) {
    for (const key of ['rrpl_user', 'user']) {
      try {
        const raw = storage.getItem(key);
        if (raw) {
          const u = JSON.parse(raw);
          if (u && u.token) return u;
        }
      } catch {
        /* ignore */
      }
    }
  }
  return null;
}

api.interceptors.request.use((config) => {
  const user = readStoredUser();
  if (user?.token) {
    config.headers.Authorization = `Bearer ${user.token}`;
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

function isAuthLoginRequest(config) {
  const url = config?.url || '';
  return url.includes('/auth/login');
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 401) {
      store.dispatch('auth/logOut').catch(() => {});

      const skipGlobalModal = isAuthLoginRequest(error.config);

      import('@/router').then(({ default: router }) => {
        const current = router.currentRoute.value;
        const needsLoginRedirect = current?.matched?.some((r) => r.meta?.authRequired);

        if (needsLoginRedirect && !skipGlobalModal) {
          import('@/services/rrplFeedback').then(({ showErrorModal }) => {
            showErrorModal({
              title: 'Sessão inválida ou expirada',
              text: 'Inicia sessão novamente para continuar no painel.',
            }).finally(() => {
              router.push({
                name: 'login',
                query: { redirectFrom: current.fullPath },
              });
            });
          });
        } else if (needsLoginRedirect) {
          router.push({
            name: 'login',
            query: { redirectFrom: current.fullPath },
          });
        }
      });
    }
    return Promise.reject(error);
  }
);

export default api;
